import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import SEO from '../components/SEO';

export default function TermsOfService() {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const sectionStyle = { marginBottom: '40px' };

  const headingStyle = {
    fontSize: '20px',
    fontWeight: 700,
    color: theme.colors.textPrimary,
    marginBottom: '12px',
  };

  const bodyStyle = {
    fontSize: '15px',
    color: theme.colors.textSecondary,
    lineHeight: '1.8',
    margin: '0 0 12px 0',
  };

  const listStyle = {
    fontSize: '15px',
    color: theme.colors.textSecondary,
    lineHeight: '1.8',
    paddingLeft: '20px',
    margin: '0 0 12px 0',
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgPrimary, color: theme.colors.textPrimary }}>
      <SEO
        title="Terms of Service | Trubonn"
        description="The terms governing your use of Trubonn's Australian-hosted cloud storage service."
        path="/terms"
      />

      {/* Nav */}
      <nav style={{
        padding: '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${theme.colors.border}`,
        position: 'sticky',
        top: 0,
        background: theme.colors.bgPrimary,
        zIndex: 1000,
      }}>
        <div onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img src="/logo.svg" alt="Trubonn Logo" style={{ height: '70px', width: '180px' }} />
        </div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <button
            onClick={() => navigate('/')}
            style={{ background: 'transparent', border: 'none', color: theme.colors.textSecondary, cursor: 'pointer', fontSize: '15px', padding: '8px 16px' }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.brand}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textSecondary}
          >
            Home
          </button>
          <button
            onClick={() => navigate('/pricing')}
            style={{ background: 'transparent', border: 'none', color: theme.colors.textSecondary, cursor: 'pointer', fontSize: '15px', padding: '8px 16px' }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.brand}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textSecondary}
          >
            Pricing
          </button>
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '80px 24px' }}>

        {/* Header */}
        <div style={{ marginBottom: '56px' }}>
          <h1 style={{
            fontSize: '48px',
            fontWeight: 800,
            marginBottom: '16px',
            background: theme.gradients.brand,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            Terms of Service
          </h1>
          <p style={{ color: theme.colors.textSecondary, fontSize: '15px', margin: 0 }}>
            Last updated: March 2026
          </p>
          <p style={{ color: theme.colors.textSecondary, fontSize: '15px', marginTop: '12px' }}>
            Please read these Terms of Service ("Terms") carefully before using Trubonn ("we", "us", "our") at trubonn.com.au. By creating an account or using our service, you agree to be bound by these Terms. If you do not agree, please do not use Trubonn.
          </p>
        </div>

        {/* 1. About Trubonn */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>1. About Trubonn</h2>
          <p style={bodyStyle}>
            Trubonn is a cloud-based file storage service that allows users to upload, organise, and share files securely online. Trubonn is operated in Australia and is available exclusively to Australian residents.
          </p>
          <p style={bodyStyle}>
            For any questions regarding these Terms, please contact us at{' '}
            <a href="mailto:admin@trubonn.com.au" style={{ color: theme.colors.brand }}>
              admin@trubonn.com.au
            </a>.
          </p>
        </div>

        {/* 2. Eligibility */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>2. Eligibility</h2>
          <p style={bodyStyle}>To use Trubonn, you must:</p>
          <ul style={listStyle}>
            <li>Be at least 18 years of age</li>
            <li>Be a resident of Australia</li>
            <li>Have the legal capacity to enter into a binding agreement</li>
            <li>Not have had a previous account terminated for breach of these Terms</li>
          </ul>
          <p style={bodyStyle}>
            By registering, you confirm that you meet these eligibility requirements. We reserve the right to verify your eligibility and suspend or terminate accounts that do not meet these criteria.
          </p>
        </div>

        {/* 3. Account Registration */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>3. Account Registration</h2>
          <p style={bodyStyle}>
            You must create an account to use Trubonn. You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account. You agree to notify us immediately at{' '}
            <a href="mailto:admin@trubonn.com.au" style={{ color: theme.colors.brand }}>
              admin@trubonn.com.au
            </a>{' '}
            if you suspect any unauthorised access to your account.
          </p>
          <p style={bodyStyle}>
            You may not share your account with others or create accounts on behalf of third parties without our written consent.
          </p>
        </div>

        {/* 4. Subscription and Billing */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>4. Subscription and Billing</h2>
          <p style={{ ...bodyStyle, fontWeight: 600, color: theme.colors.textPrimary }}>Free Trial</p>
          <p style={bodyStyle}>
            New users receive a 7-day free trial upon registration. A valid payment method is required to start the trial. You will not be charged during the trial period. If you do not cancel before the trial ends, your subscription will automatically commence and your payment method will be charged.
          </p>
          <p style={{ ...bodyStyle, fontWeight: 600, color: theme.colors.textPrimary }}>Subscription Fees</p>
          <p style={bodyStyle}>
            Following the trial, Trubonn is offered at $4.99 AUD per month, billed annually as $59.88 AUD. All prices are in Australian Dollars and are inclusive of any applicable GST.
          </p>
          <p style={{ ...bodyStyle, fontWeight: 600, color: theme.colors.textPrimary }}>Automatic Renewal</p>
          <p style={bodyStyle}>
            Your subscription renews automatically each year unless you cancel before the renewal date. We will notify you by email before your subscription renews. By subscribing, you authorise us to charge your payment method on a recurring annual basis.
          </p>
          <p style={{ ...bodyStyle, fontWeight: 600, color: theme.colors.textPrimary }}>Cancellation</p>
          <p style={bodyStyle}>
            You may cancel your subscription at any time from your account Settings page. If you cancel during the trial period, you will not be charged. If you cancel after the trial, your subscription will not be refunded and your account and all associated data will be permanently deleted immediately upon cancellation.
          </p>
          <p style={{ ...bodyStyle, fontWeight: 600, color: theme.colors.textPrimary }}>Payment Processing</p>
          <p style={bodyStyle}>
            Payments are processed securely by Stripe. We do not store your full payment details. By providing your payment information, you agree to Stripe's terms of service.
          </p>
          <p style={{ ...bodyStyle, fontWeight: 600, color: theme.colors.textPrimary }}>Failed Payments</p>
          <p style={bodyStyle}>
            If a payment fails, we will notify you by email. We may retry the payment and suspend access to your account until the outstanding amount is resolved.
          </p>
        </div>

        {/* 5. Acceptable Use Policy */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>5. Acceptable Use Policy</h2>
          <p style={bodyStyle}>
            You agree to use Trubonn only for lawful purposes and in accordance with these Terms. You must not use Trubonn to:
          </p>
          <ul style={listStyle}>
            <li>Upload, store, or share any content that is illegal, harmful, threatening, abusive, defamatory, obscene, or otherwise objectionable</li>
            <li>Infringe the intellectual property rights of any third party</li>
            <li>Upload malware, viruses, or any other malicious code</li>
            <li>Attempt to gain unauthorised access to any part of the service or other users' data</li>
            <li>Use the service to send spam or unsolicited communications</li>
            <li>Circumvent any storage limits, rate limits, or security measures</li>
            <li>Resell or sublicense access to the service without our written consent</li>
            <li>Use automated tools or bots to interact with the service without our prior approval</li>
          </ul>
          <p style={bodyStyle}>
            We reserve the right to suspend or terminate your account immediately and without notice if we determine, at our sole discretion, that you have breached this Acceptable Use Policy.
          </p>
        </div>

        {/* 6. Storage and File Limits */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>6. Storage and File Limits</h2>
          <p style={bodyStyle}>Each Trubonn account is subject to the following limits:</p>
          <ul style={listStyle}>
            <li>50 GB total storage per account</li>
            <li>Maximum file size of 500 MB per upload</li>
            <li>Maximum of 100 file uploads per hour</li>
          </ul>
          <p style={bodyStyle}>
            We reserve the right to modify these limits at any time with reasonable notice. Exceeding these limits may result in upload restrictions or account suspension.
          </p>
        </div>

        {/* 7. Your Content */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>7. Your Content</h2>
          <p style={bodyStyle}>
            You retain full ownership of all files and content you upload to Trubonn. By uploading content, you grant us a limited, non-exclusive licence to store and make your content available to you through the service.
          </p>
          <p style={bodyStyle}>
            We do not access, review, or share your files except as required to operate the service or comply with legal obligations. We are not responsible for the content you store and you are solely responsible for ensuring your content complies with applicable laws.
          </p>
        </div>

        {/* 8. Limitation of Liability */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>8. Limitation of Liability</h2>
          <p style={bodyStyle}>
            To the maximum extent permitted by Australian law, Trubonn and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use the service, including but not limited to loss of data, loss of profits, or business interruption.
          </p>
          <p style={bodyStyle}>
            Our total liability to you for any claim arising out of or relating to these Terms or the service shall not exceed the total amount you paid us in the 12 months preceding the claim.
          </p>
          <p style={bodyStyle}>
            Nothing in these Terms excludes, restricts, or modifies any rights or remedies you may have under the Australian Consumer Law, including any guarantees that cannot be excluded by law.
          </p>
        </div>

        {/* 9. Service Availability */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>9. Service Availability</h2>
          <p style={bodyStyle}>
            We aim to provide a reliable service but do not guarantee uninterrupted or error-free access. We may suspend the service temporarily for maintenance, upgrades, or circumstances beyond our control. We will endeavour to provide advance notice of planned outages where possible.
          </p>
          <p style={bodyStyle}>
            We are not responsible for any loss or damage resulting from service interruptions, data loss, or unauthorised access to your account despite our reasonable security measures.
          </p>
        </div>

        {/* 10. Account Termination */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>10. Account Termination</h2>
          <p style={bodyStyle}>
            You may delete your account at any time from the Settings page. Upon deletion, all your files, account data, and personal information will be permanently and immediately removed from our systems. This action cannot be undone. Please ensure you have downloaded any files you wish to keep before deleting your account.
          </p>
          <p style={bodyStyle}>
            We reserve the right to suspend or terminate your account at any time if you breach these Terms, engage in fraudulent activity, or if we are required to do so by law.
          </p>
        </div>

        {/* 11. Changes to the Service */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>11. Changes to the Service and Terms</h2>
          <p style={bodyStyle}>
            We may update these Terms from time to time. When we do, we will update the "Last updated" date at the top of this page and notify you by email if the changes are material. Continued use of Trubonn after changes are posted constitutes your acceptance of the updated Terms.
          </p>
          <p style={bodyStyle}>
            We may also modify, suspend, or discontinue any aspect of the service at any time. Where reasonably practicable, we will provide advance notice of significant changes.
          </p>
        </div>

        {/* 12. Governing Law */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>12. Governing Law</h2>
          <p style={bodyStyle}>
            These Terms are governed by and construed in accordance with the laws of Australia. You agree to submit to the exclusive jurisdiction of the Australian courts for the resolution of any disputes arising from these Terms or your use of Trubonn.
          </p>
          <p style={bodyStyle}>
            Nothing in these Terms limits your rights under the Australian Consumer Law or other non-excludable statutory rights applicable to you as an Australian consumer.
          </p>
        </div>

        {/* 13. Contact */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>13. Contact Us</h2>
          <p style={bodyStyle}>
            If you have any questions about these Terms, please contact us:
          </p>
          <div style={{
            background: theme.colors.bgSecondary,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: '12px',
            padding: '24px',
          }}>
            <p style={{ color: theme.colors.textPrimary, margin: '0 0 8px', fontWeight: 600 }}>Trubonn</p>
            <p style={{ color: theme.colors.textSecondary, margin: '0 0 4px', fontSize: '14px' }}>
              Email:{' '}
              <a href="mailto:admin@trubonn.com.au" style={{ color: theme.colors.brand }}>
                admin@trubonn.com.au
              </a>
            </p>
            <p style={{ color: theme.colors.textSecondary, margin: 0, fontSize: '14px' }}>
              Website:{' '}
              <a href="https://trubonn.com.au" style={{ color: theme.colors.brand }}>
                trubonn.com.au
              </a>
            </p>
          </div>
        </div>

      </div>

      {/* Footer */}
      <footer style={{
        padding: '40px',
        textAlign: 'center',
        borderTop: `1px solid ${theme.colors.border}`,
      }}>
        <p style={{ color: theme.colors.textSecondary, fontSize: '14px', margin: 0 }}>
          © 2026 Trubonn Systems. All rights reserved.
        </p>
        <div style={{ marginTop: '16px', display: 'flex', gap: '24px', justifyContent: 'center' }}>
          <a href="/privacy" style={{ color: theme.colors.textSecondary, textDecoration: 'none', fontSize: '14px' }}>Privacy Policy</a>
          <a href="/contact" style={{ color: theme.colors.textSecondary, textDecoration: 'none', fontSize: '14px' }}>Contact</a>
          <a href="/pricing" style={{ color: theme.colors.textSecondary, textDecoration: 'none', fontSize: '14px' }}>Pricing</a>
        </div>
      </footer>
    </div>
  );
}
